import { useEffect, useState } from "react";
import { ArrowDownToLine, BarChart3, Boxes, PackageCheck, TrendingUp, Truck, Wallet, Wrench } from "lucide-react";
import { api, rupiah } from "../../lib/api";

const TILES = [
  ["sent", "Terkirim", Truck, "from-sky-500/20 to-sky-500/0 text-sky-400 border-sky-500/30"],
  ["in_transit", "Dalam Perjalanan", ArrowDownToLine, "from-amber-500/20 to-amber-500/0 text-amber-400 border-amber-500/30"],
  ["installed", "Terpasang", Wrench, "from-violet-500/20 to-violet-500/0 text-violet-400 border-violet-500/30"],
  ["returned", "Diretur (Rusak)", PackageCheck, "from-rose-500/20 to-rose-500/0 text-rose-400 border-rose-500/30"],
  ["remaining", "Sisa di Teknisi Kepala", Boxes, "from-emerald-500/20 to-emerald-500/0 text-emerald-400 border-emerald-500/30"],
  ["paid_total", "Biaya Pasang Berbayar", Wallet, "from-[#F04E23]/20 to-[#F04E23]/0 text-[#F04E23] border-[#F04E23]/30"],
];

export function AssetSummaryCard({ refreshKey }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/asset-summary").then((r) => setRows(r.data)).catch(() => setRows([])); }, [refreshKey]);
  if (!rows) return null;
  const tot = TILES.reduce((a, [k]) => ({ ...a, [k]: rows.reduce((s, r) => s + (r[k] || 0), 0) }), {});
  const pct = tot.received ? Math.round((tot.installed / (rows.reduce((s, r) => s + (r.received || 0), 0) || 1)) * 100) : 0;
  const active = rows.filter((r) => r.shipments);
  const maxSent = Math.max(1, ...active.map((r) => r.sent));
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="asset-summary-card">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-2 bg-gradient-to-r from-sky-500/10 via-transparent to-[#F04E23]/10">
        <div>
          <div className="font-heading font-bold text-base md:text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5 text-sky-400" /> Rekap Nilai Aset</div>
          <div className="text-xs text-muted-foreground">Nilai barang/alat yang dikirim ke teknisi kepala, terpasang di lapangan, dan sisanya</div>
        </div>
        <div className="flex items-center gap-2 text-xs" data-testid="asset-summary-pct">
          <TrendingUp className="w-4 h-4 text-violet-400" />
          <span><b className="font-mono text-violet-400">{pct}%</b> aset yang diterima sudah terpasang</span>
        </div>
      </div>
      <div className="p-4 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {TILES.map(([k, l, Icon, cls]) => (
          <div key={k} className={`relative rounded-xl border bg-gradient-to-b p-3 ${cls}`} data-testid={`asset-summary-total-${k}`}>
            <Icon className="w-4 h-4 absolute top-3 right-3 opacity-80" />
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground pr-6">{l}</div>
            <div className="font-mono font-bold text-base md:text-lg mt-1 leading-tight">{rupiah(tot[k])}</div>
          </div>
        ))}
      </div>
      {active.length > 0 && (
        <div className="px-4 pb-4 space-y-2" data-testid="asset-summary-table">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Per Teknisi Kepala</div>
          {active.map((r) => {
            const seg = (v, c) => (r.sent ? <div className={`h-full ${c}`} style={{ width: `${(v / r.sent) * 100}%` }} /> : null);
            return (
              <div key={r.kt_user_id} className="rounded-xl border border-border px-3 py-2.5" data-testid={`asset-summary-row-${r.kt_user_id}`}>
                <div className="flex items-center justify-between gap-2 text-xs flex-wrap">
                  <div className="font-semibold text-sm">{r.kt_name} <span className="text-[10px] text-muted-foreground font-normal">· {r.shipments} kiriman · {r.usages} pemasangan</span></div>
                  <div className="font-mono text-muted-foreground">terkirim <b className="text-sky-400">{rupiah(r.sent)}</b></div>
                </div>
                <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden flex" style={{ width: `${Math.max(12, (r.sent / maxSent) * 100)}%` }} title="biru=perjalanan, ungu=terpasang, merah=retur, hijau=sisa">
                  {seg(r.in_transit, "bg-amber-400")}{seg(r.installed, "bg-violet-400")}{seg(r.returned, "bg-rose-400")}{seg(Math.max(0, r.remaining), "bg-emerald-400")}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] font-mono">
                  <span className="text-amber-400">perjalanan {rupiah(r.in_transit)}</span>
                  <span className="text-violet-400">terpasang {rupiah(r.installed)}</span>
                  <span className="text-rose-400">retur {rupiah(r.returned)}</span>
                  <span className="text-emerald-400">sisa {rupiah(r.remaining)}</span>
                  {r.paid_total > 0 && <span className="text-[#F04E23]">berbayar {rupiah(r.paid_total)}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
